import AddIcon from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import cx from "classnames";
import React, { useContext, useEffect, useMemo } from "react";

import Guide from "components/Guide";
import InferencingBackdrop from "components/Services/InferencingBackdrop";
import ShotContext from "components/Shot/ShotContext";
import { UserGuides } from "constants/constants";
import { depthMapInference } from "services/ShotService";
import { useCountdown } from "utils/hooks";

import styles from "./DepthMap.module.scss";

const CreateDepthMap: React.FC = () => {
  const { selectedShot, layers, setLayers, throttleUsage, getThrottleUsage } =
    useContext(ShotContext);
  const [isInferencing, setIsInferencing] = React.useState(false);

  const throttledTill = useMemo(() => {
    if (
      throttleUsage?.is_inference_throttled &&
      (throttleUsage.allowed_inferences || 0) < 1 &&
      throttleUsage.next_inference_provision_at
    ) {
      return new Date(throttleUsage.next_inference_provision_at);
    }
    return null;
  }, [throttleUsage]);
  const throttleCountdown = useCountdown(throttledTill, getThrottleUsage);

  const isInferenceDisabled = useMemo(() => {
    const matchingLayer = layers?.find((layer) =>
      layer.predictions.some(
        (prediction) =>
          prediction.status === "IN_PROGRESS" ||
          prediction.status === "COMPLETED",
      ),
    );
    return !!matchingLayer;
  }, [layers]);

  const handleDepthMap = async () => {
    if (!selectedShot) return;
    setIsInferencing(true);
    try {
      const layer = await depthMapInference(selectedShot.id);
      setLayers((oldLayers) =>
        [layer, ...(oldLayers?.filter((l) => l.id !== layer.id) || [])].sort(
          (l1, l2) => l2.id - l1.id,
        ),
      );
      setTimeout(() => {
        setIsInferencing(false);
      }, 6000);
    } catch (e) {
      setIsInferencing(false);
    }
  };

  return (
    <div>
      <Guide
        id={UserGuides.CreateDepthMap}
        open={layers !== undefined && !layers.length}
        placement="left"
        title="Create your first depth map"
        description="Select a shot from the shots panel and simply hit this button to generate your depth map."
      >
        <Button
          disabled={isInferenceDisabled || !!throttledTill}
          className={styles.createDepthMapBtn}
          variant="contained"
          onClick={() => {
            handleDepthMap();
          }}
          color="secondary"
          size="small"
          sx={{
            ".MuiSvgIcon-root": {
              width: "14px",
              height: "14px",
            },
          }}
        >
          <AddIcon />
          {throttleCountdown
            ? `Wait for ${throttleCountdown}`
            : "Create Depth Map"}
        </Button>
      </Guide>
      {isInferencing && (
        <InferencingBackdrop
          isInferencing={isInferencing}
          heading="Slapshot is generating your depth map..."
          subHeading="You can continue with other tasks in the meantime."
        />
      )}
    </div>
  );
};

export default CreateDepthMap;
